/* (c) Alpas Made LLC. All rights reserved.
 *
 * FAQ page behaviour for sections/kc-faq-hub.liquid, taken from the approved
 * prototype (prototypes/faq-option-a.html). Two things, both progressive
 * enhancement: the menu follows the topic you are reading, and the search box
 * filters the questions. Without this file the page is still grouped cards,
 * working anchor links and native open/close answers.
 *
 * Everything is scoped to one section root, never the document, so a second
 * copy of the section, or the theme editor re-rendering it, cannot cross wires.
 */
(function () {
  'use strict';

  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var wide = window.matchMedia('(min-width: 900px)');
  var TOLERANCE = 8;

  // Height of the site's sticky header, measured from the header itself.
  // theme.js only publishes --sticky-header-height after the desktop header
  // shrinks, so on phones that variable stays 0 while a full header sits on
  // top. Height, not bottom edge: once stuck the header's top is 0, and using
  // its bottom edge while the announcement bar is still on screen would leave
  // a bigger gap after a menu click than after scrolling.
  function headerOffset() {
    var h = document.querySelector('.header-sticky');
    if (!h) return 0;
    var pos = getComputedStyle(h).position;
    if (pos !== 'sticky' && pos !== 'fixed') return 0;
    return Math.max(0, Math.round(h.getBoundingClientRect().height));
  }

  function init(root) {
    if (!root || root.__kcfaq) return;
    var ctl = new AbortController();
    var sig = { signal: ctl.signal };
    root.__kcfaq = ctl;

    var groups = [].slice.call(root.querySelectorAll('[data-group]'));
    var links = [].slice.call(root.querySelectorAll('.kcfaq__link'));
    var nav = root.querySelector('.kcfaq__nav');
    var strip = root.querySelector('.kcfaq__list');
    var rail = root.querySelector('.kcfaq__rail');
    var list = root.querySelector('.kcfaq__groups');

    // ------------------------------------------------------------ measurements
    // One source for both numbers: where a topic lands after a menu click
    // (--kcf-land, used by scroll-margin-top) and the reading line that decides
    // which menu item is lit. The line sits just below the landing spot, so a
    // topic that was jumped to always counts as the one being read.
    var top = 0, land = 0, readLine = 0;
    function measure() {
      top = headerOffset();
      if (wide.matches) {
        land = top + 24;
        readLine = top + 120;
      } else {
        land = top + (nav ? nav.offsetHeight : 0) + 24;
        readLine = land + TOLERANCE;
      }
      root.style.setProperty('--kcf-top', top + 'px');
      root.style.setProperty('--kcf-land', land + 'px');
      // Desktop: the sticky left column (menu and help card) is taller than a
      // short last topic. Without room below that topic the column is pushed
      // up under the site header at the end of the list, so the list gets
      // exactly the missing height as bottom padding. Phones: CSS sets 0.
      if (list && rail) {
        var shown = groups.filter(function (g) { return !g.hidden; });
        var last = shown[shown.length - 1];
        var need = (wide.matches && last) ? Math.max(0, rail.offsetHeight - last.offsetHeight) : 0;
        list.style.paddingBottom = need ? need + 'px' : '';
      }
    }

    // ------------------------------------------------------------------- menu
    function markActive(el) {
      links.forEach(function (a) {
        if (a === el) a.setAttribute('aria-current', 'true');
        else a.removeAttribute('aria-current');
      });
      // Keep the lit chip in view on phones by scrolling the chip row, never
      // the page. Measured off rects, not offsetLeft.
      if (el && strip && !wide.matches && strip.scrollWidth > strip.clientWidth) {
        var want = strip.scrollLeft + (el.getBoundingClientRect().left - strip.getBoundingClientRect().left) - 16;
        want = Math.max(0, Math.min(want, strip.scrollWidth - strip.clientWidth));
        if (Math.abs(strip.scrollLeft - want) > TOLERANCE) {
          strip.scrollTo({ left: want, behavior: reduced ? 'auto' : 'smooth' });
        }
      }
    }

    // While the page scrolls to a topic that was clicked, the clicked item
    // stays lit; the scroll must not override it on the way past other topics.
    var locked = false, lockTimer = 0;
    function unlock() {
      locked = false;
      clearTimeout(lockTimer);
      spy();
    }

    function spy() {
      if (locked) return;
      var shown = groups.filter(function (g) { return !g.hidden; });
      if (!shown.length || !links.length) { markActive(null); return; }
      var best = shown[0];
      shown.forEach(function (g) {
        if (g.getBoundingClientRect().top - readLine <= 0) best = g;
      });
      // At the very bottom the last topic can never reach the line.
      if (window.innerHeight + window.scrollY >= document.documentElement.scrollHeight - 4) {
        best = shown[shown.length - 1];
      }
      var id = best.getAttribute('data-group');
      markActive(links.filter(function (a) { return a.getAttribute('data-spy') === id; })[0] || null);
    }

    var ticking = false;
    function onScroll() {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(function () { ticking = false; measure(); spy(); });
    }
    window.addEventListener('scroll', onScroll, { passive: true, signal: ctl.signal });
    window.addEventListener('resize', onScroll, sig);
    // Opening an answer moves every topic below it. `toggle` does not bubble.
    root.addEventListener('toggle', onScroll, { capture: true, signal: ctl.signal });
    links.forEach(function (a) {
      a.addEventListener('click', function () {
        measure();
        markActive(a);
        locked = true;
        clearTimeout(lockTimer);
        lockTimer = setTimeout(unlock, 1200);
        window.addEventListener('scrollend', unlock, { once: true, signal: ctl.signal });
      }, sig);
    });

    // ----------------------------------------------------------------- search
    var field = root.querySelector('[data-search]');
    var totalLine = root.querySelector('[data-total]');
    var termOut = root.querySelector('[data-term]');
    var rows = [].slice.call(root.querySelectorAll('[data-q]'));
    var TOTAL = rows.length;

    rows.forEach(function (row) {
      row._q = row.querySelector('[data-question]').textContent;
      row._a = row.querySelector('[data-answer]').textContent;
    });

    function paintMatch(el, text, term) {
      el.textContent = text;
      if (!term) return;
      var i = text.toLowerCase().indexOf(term);
      if (i < 0) return;
      var mark = document.createElement('mark');
      mark.textContent = text.slice(i, i + term.length);
      el.textContent = '';
      el.appendChild(document.createTextNode(text.slice(0, i)));
      el.appendChild(mark);
      el.appendChild(document.createTextNode(text.slice(i + term.length)));
    }

    function filter(raw) {
      var term = (raw || '').trim().toLowerCase();
      var hits = 0;

      rows.forEach(function (row) {
        var details = row.querySelector('details');
        var inQ = !!term && row._q.toLowerCase().indexOf(term) > -1;
        var inA = !!term && row._a.toLowerCase().indexOf(term) > -1;
        var hit = !term || inQ || inA;
        row.hidden = !hit;
        if (hit) hits++;
        paintMatch(row.querySelector('[data-question]'), row._q, term);
        // A question that matches only through its answer is opened, so the
        // reader can see why it is in the results. Only answers the search
        // opened are closed again; one the reader opened stays open.
        if (hit && inA && !inQ) {
          if (!details.open) { details.open = true; row._autoOpened = true; }
        } else if (row._autoOpened) {
          details.open = false;
          row._autoOpened = false;
        }
        if (!hit) details.open = false;
      });

      groups.forEach(function (g) {
        var live = [].slice.call(g.querySelectorAll('[data-q]')).filter(function (r) { return !r.hidden; });
        g.hidden = live.length === 0;
        // First and last VISIBLE rows get the edge treatment, or a filtered
        // card ends on a divider against its own border.
        [].slice.call(g.querySelectorAll('.kcfaq__details')).forEach(function (d) {
          d.classList.remove('is-first', 'is-last');
        });
        live.forEach(function (r, i) {
          var d = r.querySelector('.kcfaq__details');
          if (i === 0) d.classList.add('is-first');
          if (i === live.length - 1) d.classList.add('is-last');
        });
        var id = g.getAttribute('data-group');
        links.forEach(function (a) {
          if (a.getAttribute('data-spy') !== id) return;
          a.parentNode.hidden = g.hidden;
          a.querySelector('[data-count]').textContent = live.length;
        });
      });

      root.classList.toggle('is-filtering', !!term);
      root.classList.toggle('is-empty', hits === 0);
      if (termOut && term) termOut.textContent = raw.trim();
      if (totalLine) {
        totalLine.textContent = term ? (hits + ' of ' + TOTAL + ' answers') : (TOTAL + (TOTAL === 1 ? ' answer' : ' answers'));
      }
      measure();
      spy();
    }

    if (field) {
      field.addEventListener('input', function () { filter(field.value); }, sig);
      field.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && field.value) { field.value = ''; filter(''); }
      }, sig);
      [].slice.call(root.querySelectorAll('[data-clear]')).forEach(function (b) {
        b.addEventListener('click', function () { field.value = ''; filter(''); field.focus(); }, sig);
      });
    }

    root.classList.add('is-ready');
    measure();
    spy();
  }

  function initAll() {
    [].slice.call(document.querySelectorAll('[data-kcfaq]')).forEach(init);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll);
  else initAll();

  // The theme editor re-renders a section in place after every change. The old
  // copy's window listeners are removed before the new copy starts, so edits
  // in the editor do not pile up scroll handlers.
  document.addEventListener('shopify:section:unload', function (e) {
    [].slice.call(e.target.querySelectorAll('[data-kcfaq]')).forEach(function (root) {
      if (root.__kcfaq && root.__kcfaq.abort) root.__kcfaq.abort();
    });
  });
  document.addEventListener('shopify:section:load', initAll);
})();
